import React, {useCallback, useContext, useMemo, useState} from "react";
import {OpenDialog, dialogContext} from "./context";
import {useEventCallback} from "usehooks-ts";
import {DialogId, DialogRenderFunc, ProvidedDialog, ambientProductKey} from ".";
import {useSubState} from "../../hooks/subState";
import {ProductContext} from "../../Products/SharedPages/ProductContext";
import {ProductType} from "platformed-client/types";

type DialogContextValue = {open: OpenDialog};

type DialogProviderProps = {
  dialogs: ProvidedDialog[];
  children?: React.ReactNode | ((context: DialogContextValue) => React.ReactNode);
};

type DialogState = {open: boolean; props: any};

type DialogStates = {[dialogId: DialogId<any>]: DialogState};

export function DialogProvider({dialogs, children}: DialogProviderProps) {
  const parent = useContext(dialogContext);

  const [dialogStates, setDialogStates] = useState<DialogStates>(() =>
    Object.fromEntries(dialogs.map(([id]) => [id, {open: false, props: {}} satisfies DialogState])),
  );

  const open: OpenDialog = useEventCallback(<T,>(dialogId: DialogId<T>, dialogProps: T = {} as T) => {
    if (Object.hasOwn(dialogStates, dialogId)) {
      setDialogStates(prev => ({...prev, [dialogId]: {open: true, props: dialogProps} satisfies DialogState}));
    } else {
      parent.open(dialogId, dialogProps);
    }
  });

  const value = useMemo(() => ({open}), [open]);

  return (
    <dialogContext.Provider value={value}>
      {typeof children === "function" ? children(value) : children}

      {dialogs.map(([dialogId, renderFunc], idx) => (
        <RenderedDialog
          key={idx}
          dialogStates={dialogStates}
          setDialogStates={setDialogStates}
          dialogId={dialogId}
          renderFunc={renderFunc}
        />
      ))}
    </dialogContext.Provider>
  );
}

function RenderedDialog<T>({
  dialogId,
  renderFunc,
  dialogStates,
  setDialogStates,
}: {
  dialogId: DialogId<T>;
  renderFunc: DialogRenderFunc<T>;
  dialogStates: DialogStates;
  setDialogStates: React.Dispatch<React.SetStateAction<DialogStates>>;
}) {
  const [dialogState, setDialogState] = useSubState(dialogStates, setDialogStates, s => s.property(dialogId));

  const open = dialogState.open;
  const onClose = useCallback(() => setDialogState(s => ({...s, open: false})), [setDialogState]);
  const onCloseComplete = useCallback(() => setDialogState(s => ({...s, props: {}})), [setDialogState]);

  // Strip the ambient product captured at `open()` time and re-provide
  // `ProductContext` so product-aware components inside the dialog (e.g. user
  // pickers) behave as if they were still in the product tree.
  const {[ambientProductKey]: ambientProduct, ...props} = dialogState.props ?? {};

  return (
    <ProductContext.Provider value={(ambientProduct as ProductType) ?? "All"}>
      {renderFunc({open, onClose, onCloseComplete, ...props})}
    </ProductContext.Provider>
  );
}
